/**
 * CampaignAudienceBuilder.jsx
 * ───────────────────────────
 * Re-imagined Step 2 of the Sales-Dialer "Create campaign" lightbox.
 *
 * Goals (per user May-04-2026 directive):
 *   • Cleaner, well-formatted 2-column layout using the global Apple-
 *     fluid CSS-variable system instead of inline pre-overhaul styles.
 *   • Mirror the filter taxonomy of the redesigned Lead Focus (NPPES,
 *     Practice, Medicare, EHR, signals) — the same field surface so
 *     audiences are now thinkable in lead-focus vocabulary.
 *   • New top-of-form AI campaign-builder input — admins type plain-
 *     english intent ("Build a CCM call campaign for Internal Medicine
 *     in NY with 500+ Medicare beneficiaries") and the form fills in.
 *   • Keep all existing filter state shape so downstream Step 3-5 +
 *     audience preview keep working untouched.
 *
 * Frontend-only / fully-mocked. The "AI parse" calls a local heuristic
 * function (see `parseCampaignBrief()` below). No backend round-trip.
 */
import React, { useEffect, useMemo, useState } from "react";import { Target, Loader2, Sparkles, X, Wand2, Save, Bookmark, ChevronRight, ChevronDown, ExternalLink, MinusCircle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { getLeadScore } from "@/lib/getLeadScore";
import { SUB_STATUSES } from "@/lib/pipelineStages";

const STAGES = ["new", "contacted", "discovery", "demo", "proposal", "negotiation", "closed_won", "closed_lost"];
const SOURCES = ["inbound", "referral", "outbound", "website", "event", "partner"];
const PRODUCTS = ["mdnet", "pms", "revq", "ccm", "apcm", "rpm", "reach"];
const TIERS = ["A", "B", "C"];
const RATINGS = ["Hot", "Warm", "Cold"];
const PRACTICE_TYPES = ["Primary care", "Specialty practice", "Multi-specialty", "Urgent care", "ACO / IDN", "Behavioral health"];
const SIZE_BANDS = ["Solo provider", "Small group (2–5)", "Multi-provider (6–25)", "Large group (26–100)", "ACO / IDN (100+)"];
const SPECIALTIES = ["Cardiology", "Internal Medicine", "Family Medicine", "Pulmonology", "Pediatrics", "Endocrinology", "Nephrology", "Geriatrics"];
const EHR_VENDORS = ["No certified EHR", "Epic", "Cerner", "Athena", "eClinicalWorks", "NextGen", "Other"];
const SIGNAL_FIELDS = [
  { id: "medicare_assignment", label: "Medicare assignment" },
  { id: "pqrs", label: "PQRS participating" },
  { id: "erx", label: "eRx participating" },
  { id: "telehealth", label: "Telehealth" },
  { id: "board_cert", label: "Board certified" },
  { id: "mips", label: "MIPS attested" },
];
const STATES_LIST = ["AL","AK","AZ","AR","CA","CO","CT","DE","DC","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

/* ── Plain-english campaign brief parser (mocked agent) ─── */
function parseCampaignBrief(brief, current = {}) {
  const t = brief.toLowerCase();
  const out = { ...current };
  const reasons = [];

  // Specialty / specialty group
  for (const sp of SPECIALTIES) {
    if (t.includes(sp.toLowerCase())) {
      out.specialty_groups = Array.from(new Set([...(out.specialty_groups || []), sp]));
      reasons.push(`Specialty: ${sp}`);
    }
  }

  // Product interest
  const productAliases = { ccm: "chronic care management", rpm: "remote patient monitoring", apcm: "advanced primary care" };
  for (const p of PRODUCTS) {
    const alias = productAliases[p];
    const pattern = alias ? `\\b${p}\\b|\\b${alias}\\b` : `\\b${p}\\b`;
    if (new RegExp(pattern, "i").test(brief)) {
      out.product_interest = Array.from(new Set([...(out.product_interest || []), p]));
      reasons.push(`Product: ${p.toUpperCase()}`);
    }
  }

  // Stages
  for (const s of STAGES) {
    if (t.includes(s.replace("_", " "))) {
      out.stages = Array.from(new Set([...(out.stages || []), s]));
      reasons.push(`Stage: ${s.replace("_", " ")}`);
    }
  }

  // States — match by full name OR 2-letter code
  const STATE_NAMES = { "new york": "NY", california: "CA", texas: "TX", florida: "FL", "north carolina": "NC", georgia: "GA", "illinois": "IL", massachusetts: "MA", "indiana": "IN", "alabama": "AL", "louisiana": "LA", "arizona": "AZ" };
  for (const [name, code] of Object.entries(STATE_NAMES)) {
    if (t.includes(name) || new RegExp(`\\b${code}\\b`).test(brief)) {
      out.states = Array.from(new Set([...(out.states || []), code]));
      reasons.push(`State: ${code}`);
    }
  }

  // Cities — quick heuristic for capitalized words after "in"
  const cityMatch = brief.match(/\bin\s+([A-Z][a-zA-Z]+(?:\s+[A-Z][a-zA-Z]+)?)\b/);
  if (cityMatch && !STATE_NAMES[cityMatch[1].toLowerCase()] && cityMatch[1].length > 2) {
    out.cities = Array.from(new Set([...(out.cities || []), cityMatch[1]]));
    reasons.push(`City: ${cityMatch[1]}`);
  }

  // Medicare beneficiaries threshold
  const beneMatch = t.match(/(\d{2,5})\+?\s*(?:medicare|patient|benes|beneficiaries)/);
  if (beneMatch) {
    out.medicare_min = parseInt(beneMatch[1], 10);
    reasons.push(`Medicare benes ≥ ${out.medicare_min}`);
  }

  // Group size
  const groupMatch = t.match(/(\d{1,3})\+?\s*(?:provider|fte|clinician|physician|doctor)/);
  if (groupMatch) {
    out.group_size_min = parseInt(groupMatch[1], 10);
    reasons.push(`Group size ≥ ${out.group_size_min}`);
  }

  // Score
  const scoreMatch = t.match(/(?:hot|score|tier).*?(\d{2,3})/);
  if (scoreMatch) {
    out.score_min = parseInt(scoreMatch[1], 10);
    reasons.push(`Score ≥ ${out.score_min}`);
  }
  if (/tier\s*a|hot lead/.test(t)) {
    out.tiers = Array.from(new Set([...(out.tiers || []), "A"]));
    reasons.push("Tier: A");
  }

  // EHR
  if (/no\s+(certified\s+)?ehr|greenfield/.test(t)) {
    out.ehr_vendors = Array.from(new Set([...(out.ehr_vendors || []), "No certified EHR"]));
    reasons.push("EHR: greenfield");
  }
  for (const v of ["Epic", "Cerner", "Athena", "NextGen"]) {
    if (t.includes(v.toLowerCase())) {
      out.ehr_vendors = Array.from(new Set([...(out.ehr_vendors || []), v]));
      reasons.push(`EHR: ${v}`);
    }
  }

  // Signals
  if (/mips/.test(t)) { out.signals = Array.from(new Set([...(out.signals || []), "mips"])); reasons.push("Signal: MIPS"); }
  if (/erx|electronic.{0,4}prescr/.test(t)) { out.signals = Array.from(new Set([...(out.signals || []), "erx"])); reasons.push("Signal: eRx"); }
  if (/telehealth|virtual visit/.test(t)) { out.signals = Array.from(new Set([...(out.signals || []), "telehealth"])); reasons.push("Signal: telehealth"); }

  return { filters: out, reasons };
}

/* ── Pill primitives ─────────────────────────────────────── */
function ChipBtn({ active, onClick, children, testid, tone = "blue" }) {
  const tones = {
    blue:   { activeBg: "var(--blue-soft)", activeColor: "var(--blue)", activeBorder: "#cfe2ff" },
    success:{ activeBg: "var(--success-soft)", activeColor: "#065f46", activeBorder: "#bbf7d0" },
    accent: { activeBg: "#F4EAFE", activeColor: "#5B6CFF", activeBorder: "#E5DCFA" },
    neutral:{ activeBg: "var(--navy)", activeColor: "#fff", activeBorder: "var(--navy)" },
    danger: { activeBg: "#FEE2E2", activeColor: "#A32D2D", activeBorder: "#FCA5A5" },
    teal:   { activeBg: "#E1F5EE", activeColor: "#0F6E56", activeBorder: "#B5E5D2" },
  };
  const t = tones[tone];
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        padding: "4px 11px", borderRadius: 999, fontSize: 11, fontWeight: 500,
        border: active ? `0.5px solid ${t.activeBorder}` : "0.5px solid var(--border)",
        background: active ? t.activeBg : "var(--paper)",
        color: active ? t.activeColor : "var(--text-3)",
        cursor: "pointer", fontFamily: "inherit",
        transition: "background-color 120ms ease, color 120ms ease, border-color 120ms ease",
        textTransform: "capitalize",
      }}
    >{children}</button>
  );
}

function FieldGroup({ eyebrow, hint, children }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 6 }}>
        <span className="eyebrow">{eyebrow}</span>
        {hint && <span style={{ fontSize: 10, color: "var(--text-4)" }}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

/* ── AI campaign brief panel ─────────────────────────────── */
// Maps backend `/api/ai/segment-parse` conditions[] → campaign-builder flat
// filter shape. Different field names but compatible domain.
function conditionsToCampaignFilters(conditions, current = {}) {
  const out = { ...current };
  const reasons = [];
  for (const c of conditions || []) {
    if (!c || !c.field) continue;
    switch (c.field) {
      case "specialty":
        out.specialty_groups = Array.from(new Set([...(out.specialty_groups || []), ...(c.value || [])]));
        for (const v of c.value || []) reasons.push(`Specialty: ${v}`);
        break;
      case "product_fit": {
        const map = { CCM: "ccm", APCM: "apcm", RPM: "rpm", AWV: "reach", EHR: "mdnet", PM: "pms", RCM: "revq" };
        const ps = (c.value || []).map((v) => map[v] || v.toLowerCase()).filter(Boolean);
        out.product_interest = Array.from(new Set([...(out.product_interest || []), ...ps]));
        for (const v of c.value || []) reasons.push(`Product: ${v}`);
        break;
      }
      case "state":
        out.states = Array.from(new Set([...(out.states || []), ...(c.value || [])]));
        for (const v of c.value || []) reasons.push(`State: ${v}`);
        break;
      case "rating": {
        const ratingToTier = { Hot: "A", Warm: "B", Cold: "C" };
        const tiers = (c.value || []).map((v) => ratingToTier[v]).filter(Boolean);
        if (tiers.length) {
          out.tiers = Array.from(new Set([...(out.tiers || []), ...tiers]));
          for (const v of c.value || []) reasons.push(`Tier: ${v}`);
        }
        break;
      }
      case "ai_score":
        if (typeof c.min === "number") { out.score_min = c.min; reasons.push(`Score ≥ ${c.min}`); }
        if (typeof c.max === "number" && c.max < 100) { out.score_max = c.max; reasons.push(`Score ≤ ${c.max}`); }
        break;
      case "medicare_benes": {
        // LLM emits BOTH min & max; surface ranges (e.g. 0–100, 500–99999)
        const hasMin = typeof c.min === "number" && c.min > 0;
        const hasMax = typeof c.max === "number" && c.max < 99999;
        if (hasMin) out.medicare_min = c.min;
        if (hasMax) out.medicare_max = c.max;
        if (hasMin && hasMax) reasons.push(`Medicare benes: ${c.min}–${c.max}`);
        else if (hasMin) reasons.push(`Medicare benes ≥ ${c.min}`);
        else if (hasMax) reasons.push(`Medicare benes ≤ ${c.max}`);
        else if (typeof c.min === "number" || typeof c.max === "number") {
          // Explicit "0-100" — preserve the upper bound even though min=0
          if (typeof c.max === "number" && c.max < 99999) { out.medicare_max = c.max; reasons.push(`Medicare benes ≤ ${c.max}`); }
        }
        break;
      }
      case "group_size": {
        const hasMin = typeof c.min === "number" && c.min > 0;
        const hasMax = typeof c.max === "number" && c.max < 9999;
        if (hasMin) out.group_size_min = c.min;
        if (hasMax) out.group_size_max = c.max;
        if (hasMin && hasMax) reasons.push(`Group size: ${c.min}–${c.max}`);
        else if (hasMin) reasons.push(`Group size ≥ ${c.min}`);
        else if (hasMax) reasons.push(`Group size ≤ ${c.max}`);
        break;
      }
      case "last_contact_days":
        if (typeof c.min === "number" && c.min > 0) { out.last_contact_days_min = c.min; reasons.push(`No contact ≥ ${c.min}d`); }
        break;
      case "ehr_vendor":
        out.ehr_vendors = Array.from(new Set([...(out.ehr_vendors || []), ...(c.value || [])]));
        for (const v of c.value || []) reasons.push(`EHR: ${v}`);
        break;
      case "signals":
        out.signals = Array.from(new Set([...(out.signals || []), ...(c.value || [])]));
        for (const v of c.value || []) reasons.push(`Signal: ${v}`);
        break;
      case "owner_availability":
        // Owner-availability is applied server-side via LUMINA's
        // mongo_query (the audience preview uses the pinned matched_ids);
        // here we only surface a human-readable chip.
        for (const v of c.value || []) reasons.push(v === "unassigned" ? "Owner: unassigned" : "Owner: assigned");
        break;
      default:
        break;
    }
  }
  return { filters: out, reasons };
}

function AIBriefPanel({ filters, onApply, onLuminaPin }) {
  const [brief, setBrief] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [viaLLM, setViaLLM] = useState(false);
  // Iter 255 (Jul 2026): "Save as reusable audience" — piggybacks on
  // the existing LUMINA templates API (`/api/leads/lumina/templates`).
  // Reps who craft the perfect NL query for one campaign can pin it
  // as a named audience and re-launch a fresh campaign against the
  // same criteria in one click a week later.
  const [savedAudiences, setSavedAudiences] = useState([]);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveShared, setSaveShared] = useState(false);
  const [savingAudience, setSavingAudience] = useState(false);

  // Load saved audiences on mount so the "Load saved" dropdown is
  // populated immediately.
  useEffect(() => {
    (async () => {
      try {
        const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
        const tk = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
        const { default: axios } = await import("axios");
        const res = await axios.get(`${apiBase}/leads/lumina/templates`, {
          headers: tk ? { Authorization: `Bearer ${tk}` } : {},
          timeout: 10000,
        });
        setSavedAudiences(res.data?.items || []);
      } catch (_) {
        // Non-fatal — the panel still works without the load-saved list.
      }
    })();
  }, []);

  const examples = [
    // Iter 254 (Jul 2026): refreshed to showcase today's LUMINA fixes —
    // WON/LOST stage filter, owner-based, last-contact-date (ET-anchored),
    // Ampliz-joined medicare fields. Same engine as /leads AI Search so
    // whatever queries work there work here.
    "Cardiology practices in TX with 500+ Medicare benes, never contacted",
    "Won leads in Internal Medicine with pipeline value over $100K",
    "Family Medicine in CA using Athena EHR, last contacted more than 30 days ago",
    "Hot rating leads owned by priya, MIPS attested, in FL or GA",
  ];

  const submit = async () => {
    if (!brief.trim()) { toast.error("Type a campaign brief first"); return; }
    setBusy(true);
    setViaLLM(false);
    // ── Iter 138 (jmani@ehiehr.com bug d9578bdd) — Lumina is now the
    //    primary NL→filters engine. Lumina has stricter "emit ONLY what
    //    the user said" rules (the legacy /ai/segment-parse kept
    //    hallucinating extra rating/score/region filters). On Lumina
    //    failure (Azure outage / unsupported query / empty conditions)
    //    we fall back to the legacy endpoint to keep parity.
    let parsed = null;
    let luminaMeta = null;
    let luminaPinIds = null;
    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
    const tk = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
    const { default: axios } = await import("axios");
    try {
      const res = await axios.post(
        `${apiBase}/dialer/ai-brief-parse`,
        { prompt: brief },
        { headers: tk ? { Authorization: `Bearer ${tk}` } : {}, timeout: 22000 }
      );
      const d = res.data || {};
      luminaMeta = d.lumina || null;
      if (d.source === "lumina" && Array.isArray(d.conditions) && d.conditions.length) {
        const mapped = conditionsToCampaignFilters(d.conditions, filters);
        if (mapped.reasons.length) {
          parsed = mapped;
          setViaLLM(true);
        }
      }
      // Iter 139: capture Lumina-matched lead IDs (includes fulltext-
      //   fallback hits like "COPD") so the audience preview shows the
      //   real Lumina audience even when the dialer's structured filters
      //   can't represent every signal. Without this the right-side
      //   "Audience preview" reads 0 even though Lumina found leads —
      //   exactly the bug the user reported on 2026-05-25.
      // Iter 271 (Jul 2026): Also pin when Lumina AUTHORITATIVELY
      //   matched 0 leads. Previously an empty array left `luminaPinIds`
      //   as null → parent cleared any existing pin → audience preview
      //   fell back to unfiltered manual filters (e.g. "contacted by
      //   priyanka in last 11 days" would say "matched 0" in the AI
      //   panel but the preview beside it showed 2,731 leads). We now
      //   distinguish "Lumina didn't run" (null pin, use filters) from
      //   "Lumina ran and matched nothing" (empty-array pin, show 0).
      const luminaRan = !!(luminaMeta && !luminaMeta.error && luminaMeta.mongo_query
        && Object.keys(luminaMeta.mongo_query || {}).length > 0
        && typeof luminaMeta.matched_count === "number");
      if (luminaRan) {
        luminaPinIds = Array.isArray(luminaMeta.matched_ids) ? luminaMeta.matched_ids : [];
      }
    } catch (_) {
      parsed = null;
    }
    // ── Fallback: legacy /ai/segment-parse (kept until Lumina production
    //    confidence is at 100%). Same call site as before.
    if (!parsed) {
      try {
        const res = await axios.post(
          `${apiBase}/ai/segment-parse`,
          { prompt: brief },
          { headers: tk ? { Authorization: `Bearer ${tk}` } : {}, timeout: 18000 }
        );
        const d = res.data || {};
        const mapped = conditionsToCampaignFilters(d.conditions || [], filters);
        if (mapped.reasons.length) {
          parsed = mapped;
          if (d.source === "llm") setViaLLM(true);
        }
      } catch (_e) {
        parsed = null;
      }
    }
    // Last-resort: local heuristic.
    if (!parsed) parsed = parseCampaignBrief(brief, filters);
    setBusy(false);
    // Iter 271 (Jul 2026): if LUMINA authoritatively pinned an empty
    // audience, honor that instead of falling back to filter heuristics
    // (which would show unrelated leads and mislead the rep).
    const luminaPinnedEmpty = Array.isArray(luminaPinIds) && luminaPinIds.length === 0
      && luminaMeta && !luminaMeta.error && typeof luminaMeta.matched_count === "number";
    if (parsed.reasons.length === 0 && !luminaPinIds && !luminaPinnedEmpty) {
      setResult({ ...parsed, lumina: luminaMeta });
      onLuminaPin?.(null, luminaMeta); // ensure any previous pin is cleared
      toast.info("Could not infer filters — try a more specific brief");
      return;
    }
    // ── Auto-apply: the live preview + Active Filters panel update
    // immediately. The previous 2-step Apply UX confused users into
    // thinking the parse had failed.
    setResult({ ...parsed, lumina: luminaMeta });
    onApply(parsed.filters);
    // Iter 139: emit Lumina pin to the parent so the audience preview
    // can override `applyCampaignFilters` with the matched_ids list
    // when Lumina found leads via fulltext fallback.
    onLuminaPin?.(luminaPinIds, luminaMeta);
    if (Array.isArray(luminaPinIds) && luminaPinIds.length) {
      toast.success(`Lumina pinned ${luminaPinIds.length} matching lead${luminaPinIds.length === 1 ? "" : "s"}${luminaMeta?.fallback_used ? " · fulltext" : ""} — preview locked to AI audience`);
    } else if (luminaPinnedEmpty) {
      toast.info("Lumina matched 0 leads — audience locked to empty. Loosen your criteria and try again.");
    } else {
      toast.success(`AI applied ${parsed.reasons.length} filter${parsed.reasons.length === 1 ? "" : "s"} — lead preview refreshing`);
    }
  };

  return (
    <div data-testid="campaign-ai-panel" style={{
      padding: 14, borderRadius: 12,
      background: "linear-gradient(135deg, #EEF6FF 0%, #F4EAFE 100%)",
      border: "0.5px solid #cfe2ff", marginBottom: 14,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ width: 26, height: 26, borderRadius: 8, background: "linear-gradient(135deg, var(--blue), #7c3aed)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Wand2 style={{ width: 13, height: 13, color: "#fff" }} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>Describe your campaign in plain English</div>
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 2 }}>
            Powered by <strong>LUMINA</strong> — the same natural-language engine as the AI Search on <code>/leads</code>.
            Whatever queries work there work here: stages (WON/LOST), owners, last-contact windows, Medicare benes, EHR vendors, specialties, states, and clinical signals.
          </div>
        </div>
      </div>
      <textarea
        data-testid="campaign-ai-brief"
        rows={5}
        value={brief}
        onChange={(e) => setBrief(e.target.value)}
        placeholder='e.g. "Cardiologists in TX who used Athena last year, 500+ Medicare benes" or "Family Medicine leads contacted in last 7 days with no follow-up"'
        className="input"
        style={{ resize: "vertical", fontSize: 12, fontFamily: "inherit", marginBottom: 8, minHeight: 110, lineHeight: 1.45 }}
      />
      <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
        {examples.map((e, i) => (
          <button key={i} data-testid={`campaign-ai-example-${i}`} onClick={() => setBrief(e)} style={{ fontSize: 10, padding: "3px 8px", borderRadius: 999, background: "var(--paper)", border: "0.5px solid var(--border)", color: "var(--text-3)", cursor: "pointer", fontFamily: "inherit", maxWidth: 220, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={e}>
            {e}
          </button>
        ))}
      </div>
      {/* Iter 255 (Jul 2026): saved audience picker. Shows only when the
          rep has at least one saved audience. Clicking a chip prefills
          the brief and re-runs the AI parse so the audience pin lands
          in the campaign builder just like a fresh submission. */}
      {savedAudiences.length > 0 && (
        <div
          data-testid="campaign-ai-saved-audiences"
          style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}
        >
          <div style={{ fontSize: 10, fontWeight: 600, color: "#3C3489", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <Bookmark style={{ width: 11, height: 11 }} /> Saved audiences
          </div>
          {savedAudiences.slice(0, 8).map((a, i) => (
            <button
              key={a.id}
              data-testid={`campaign-ai-saved-${i}`}
              title={`${a.query || ""}${a.owner_name ? ` · by ${a.owner_name}` : ""}${a.used_count ? ` · used ${a.used_count}×` : ""}`}
              onClick={() => {
                setBrief(a.query || "");
                // Auto-run so the audience lands immediately.
                setTimeout(submit, 0);
              }}
              style={{
                fontSize: 10, padding: "3px 10px", borderRadius: 999,
                background: "#EEEDFE", border: "0.5px solid #CECBF6",
                color: "#3C3489", cursor: "pointer", fontFamily: "inherit",
                maxWidth: 200, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                display: "inline-flex", alignItems: "center", gap: 4,
              }}
            >
              {a.name}
              {a.shared && !a.is_owner && (
                <span style={{ fontSize: 8, opacity: 0.7 }}>·shared</span>
              )}
            </button>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button
          data-testid="campaign-ai-parse"
          onClick={submit}
          disabled={busy || !brief.trim()}
          className="btn btn-primary"
          style={{ padding: "6px 14px", fontSize: 12, opacity: busy || !brief.trim() ? 0.6 : 1 }}
        >
          {busy ? <><Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} /> Parsing…</> : <><Sparkles style={{ width: 12, height: 12 }} /> Parse with AI & apply</>}
        </button>
        {result && result.reasons.length > 0 && (
          <span data-testid="campaign-ai-applied-badge" style={{ fontSize: 10.5, fontWeight: 600, color: "#1a7a52", display: "inline-flex", alignItems: "center", gap: 4 }}>
            ✓ {result.reasons.length} filter{result.reasons.length === 1 ? "" : "s"} applied
          </span>
        )}
        {/* Save-as-audience button — only rendered after a successful
            LUMINA parse so we know we have a valid query + mongo_cache. */}
        {result && result.lumina && result.lumina.mongo_query && Object.keys(result.lumina.mongo_query).length > 0 && (
          <button
            data-testid="campaign-ai-save-audience"
            onClick={() => {
              setSaveName(result?.lumina?.suggested_name || "");
              setSaveShared(false);
              setShowSaveModal(true);
            }}
            style={{
              marginLeft: "auto",
              fontSize: 11, padding: "5px 10px", borderRadius: 6,
              background: "#fff", border: "0.5px solid #CECBF6",
              color: "#3C3489", cursor: "pointer", fontFamily: "inherit",
              display: "inline-flex", alignItems: "center", gap: 4,
            }}
          >
            <Save style={{ width: 11, height: 11 }} /> Save as audience
          </button>
        )}
      </div>
      {result && (result.reasons.length > 0 || typeof result?.lumina?.matched_count === "number") && (
        <div data-testid="campaign-ai-reasons" style={{ marginTop: 10, padding: 10, background: "var(--paper)", borderRadius: 8, border: "0.5px solid var(--border-soft)" }}>
          <div className="eyebrow" style={{ marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <span>AI inferred</span>
            {viaLLM && <span data-testid="campaign-ai-llm-tag" style={{ padding: "1px 6px", fontSize: 9, fontWeight: 700, borderRadius: 999, background: "linear-gradient(135deg,#EEF6FF,#F4EAFE)", color: "#5B6CFF" }}>LUMINA</span>}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {result.reasons.map((r, i) => (
              <span key={i} style={{ padding: "2px 8px", fontSize: 10, fontWeight: 500, borderRadius: 999, background: "var(--blue-soft)", color: "var(--blue)" }}>{r}</span>
            ))}
          </div>
          {Array.isArray(result?.lumina?.normalization_notes) && result.lumina.normalization_notes.length > 0 && (
            <div data-testid="campaign-ai-lumina-notes" style={{ marginTop: 8, padding: "6px 8px", background: "rgba(91,108,255,0.06)", borderRadius: 6, border: "0.5px solid rgba(91,108,255,0.2)" }}>
              <div style={{ fontSize: 9.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.4px", color: "#5B6CFF", marginBottom: 4 }}>How Lumina interpreted you</div>
              {result.lumina.normalization_notes.slice(0, 4).map((n, i) => (
                <div key={i} style={{ fontSize: 10, color: "var(--text-2)", lineHeight: 1.4 }}>• {n}</div>
              ))}
            </div>
          )}
          {/* Iter 138 — Lumina live executor result. When the structured
              query returned 0 we fall back to fulltext across the AI
              reasoning + free-text fields where conditions like COPD
              actually live in this corpus. Surface the real count + the
              fallback flag so the rep can see "Lumina found N leads"
              alongside the flat-filter preview number above. */}
          {result?.lumina && typeof result.lumina.matched_count === "number" && (
            <div data-testid="campaign-ai-lumina-count" style={{
              marginTop: 8, padding: "8px 10px", borderRadius: 6,
              background: result.lumina.fallback_used ? "rgba(186,117,23,0.08)" : "rgba(48,209,88,0.08)",
              border: `0.5px solid ${result.lumina.fallback_used ? "rgba(186,117,23,0.32)" : "rgba(48,209,88,0.32)"}`,
            }}>
              <div style={{ fontSize: 10.5, color: "var(--text-2)", lineHeight: 1.5 }}>
                <strong style={{ color: result.lumina.fallback_used ? "#7c4a04" : "#1a7f3c" }}>
                  Lumina matched {result.lumina.matched_count} {result.lumina.matched_count === 1 ? "lead" : "leads"}
                </strong>
                {result.lumina.fallback_used && Array.isArray(result.lumina.fallback_keywords) && result.lumina.fallback_keywords.length > 0 && (
                  <span> via fulltext fallback on <em>{result.lumina.fallback_keywords.join(", ")}</em> (your audience has no structured signals for those terms; we searched the AI reasoning + free-text fields instead)</span>
                )}
                {!result.lumina.fallback_used && result.lumina.matched_count === 0 && (
                  <span> — try loosening one of your filters; the structured criteria didn&apos;t match any leads in this corpus.</span>
                )}
              </div>
              {/* Iter 269 (Jul 2026) — audience-too-broad warning. Fires
                  when the matched total exceeds the 10K full-ID ceiling
                  so the rep knows to narrow the criteria before saving
                  a campaign that would silently exclude some leads. */}
              {result.lumina.ceiling_hit && Array.isArray(result.lumina.matched_ids) && (
                <div data-testid="campaign-ai-ceiling-warning" style={{
                  marginTop: 6, padding: "6px 10px", borderRadius: 6,
                  background: "rgba(219,68,55,0.08)",
                  border: "0.5px solid rgba(219,68,55,0.35)",
                }}>
                  <div style={{ fontSize: 10.5, color: "#7c1d13", lineHeight: 1.5 }}>
                    <strong>⚠ Audience too broad</strong> — capped at {result.lumina.matched_ids.length.toLocaleString()} of {result.lumina.matched_count.toLocaleString()} matches. Narrow your criteria (region, specialty, medicare range) or split into multiple campaigns for full coverage.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      {result && result.reasons.length === 0 && typeof result?.lumina?.matched_count !== "number" && (
        <div data-testid="campaign-ai-noresult" style={{ marginTop: 8, fontSize: 11, color: "var(--text-3)", fontStyle: "italic" }}>
          Couldn&apos;t infer specific filters — try mentioning specialty, region, Medicare benes, or product (CCM / RPM / EHR).
        </div>
      )}

      {/* Iter 255 (Jul 2026): Save-as-audience modal.
          Persists the current NL query + cached mongo/evidence/notes
          via the existing /api/leads/lumina/templates endpoint so this
          saved audience is also reusable from the /leads AI Search
          side (one shared template pool across both surfaces). */}
      {showSaveModal && (
        <div
          onClick={() => setShowSaveModal(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 200,
          }}
        >
          <div
            data-testid="campaign-ai-save-modal"
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: 12, padding: "18px 22px",
              width: 420, maxWidth: "90vw",
              boxShadow: "0 20px 50px rgba(0,0,0,0.2)",
            }}
          >
            <div style={{ fontSize: 15, fontWeight: 600, color: "#0f172a", marginBottom: 6 }}>
              Save as reusable audience
            </div>
            <div style={{ fontSize: 11, color: "#64748b", marginBottom: 14, lineHeight: 1.4 }}>
              Pin this LUMINA query so you can re-launch a campaign against the same criteria in one click.
              Saved audiences appear on both this Dialer builder and the <code>/leads</code> AI Search.
            </div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "#334155", marginBottom: 4 }}>Name</label>
            <input
              data-testid="campaign-ai-save-name"
              autoFocus
              value={saveName}
              onChange={(e) => setSaveName(e.target.value)}
              placeholder='e.g. "TX cardiologists · high benes"'
              style={{
                width: "100%", padding: "8px 10px", fontSize: 12,
                border: "0.5px solid #cbd5e1", borderRadius: 6,
                fontFamily: "inherit", outline: "none", boxSizing: "border-box",
              }}
            />
            <div style={{ marginTop: 10, fontSize: 10, color: "#94a3b8" }}>
              Saved from query: <em>“{brief.slice(0, 120)}{brief.length > 120 ? "…" : ""}”</em>
            </div>
            <label
              style={{
                display: "flex", alignItems: "center", gap: 6,
                marginTop: 14, cursor: "pointer", fontSize: 12, color: "#334155",
              }}
            >
              <input
                data-testid="campaign-ai-save-shared"
                type="checkbox"
                checked={saveShared}
                onChange={(e) => setSaveShared(e.target.checked)}
                style={{ accentColor: "#3C3489", cursor: "pointer" }}
              />
              Share with the rest of the team (they can load it, only you can delete it)
            </label>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 18 }}>
              <button
                data-testid="campaign-ai-save-cancel"
                onClick={() => setShowSaveModal(false)}
                style={{
                  padding: "6px 14px", fontSize: 12, borderRadius: 6,
                  background: "#fff", border: "0.5px solid #cbd5e1",
                  color: "#334155", cursor: "pointer", fontFamily: "inherit",
                }}
              >
                Cancel
              </button>
              <button
                data-testid="campaign-ai-save-submit"
                disabled={savingAudience || !saveName.trim()}
                onClick={async () => {
                  if (!saveName.trim()) return;
                  setSavingAudience(true);
                  try {
                    const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
                    const tk = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
                    const { default: axios } = await import("axios");
                    const body = {
                      name: saveName.trim(),
                      query: brief,
                      mongo_cache: result?.lumina?.mongo_query || {},
                      evidence_cache: result?.lumina?.evidence || [],
                      normalization_notes_cache: result?.lumina?.normalization_notes || [],
                      shared: saveShared,
                    };
                    const res = await axios.post(
                      `${apiBase}/leads/lumina/templates`,
                      body,
                      { headers: tk ? { Authorization: `Bearer ${tk}` } : {}, timeout: 12000 }
                    );
                    setSavedAudiences((prev) => [{ ...res.data, is_owner: true }, ...prev]);
                    toast.success(`Saved audience "${saveName.trim()}" — reusable from Dialer + /leads`);
                    setShowSaveModal(false);
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Couldn't save the audience — try again.");
                  } finally {
                    setSavingAudience(false);
                  }
                }}
                style={{
                  padding: "6px 14px", fontSize: 12, borderRadius: 6,
                  background: "linear-gradient(135deg, var(--blue), #7c3aed)",
                  border: "none", color: "#fff", cursor: savingAudience ? "wait" : "pointer",
                  fontFamily: "inherit", opacity: savingAudience || !saveName.trim() ? 0.6 : 1,
                  display: "inline-flex", alignItems: "center", gap: 5,
                }}
              >
                {savingAudience
                  ? <><Loader2 style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} /> Saving…</>
                  : <><Save style={{ width: 11, height: 11 }} /> Save audience</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Active filters populator ─────────────────────────────
   Aggregates EVERY currently-set filter into a single visible
   pill bar at the top of the audience step so users can:
     • Confirm exactly what filters AI just applied
     • Remove any individual filter (× on each pill)
     • Clear all and start over
   Backed by the same `setFilter` / `toggleArrayFilter` props
   so it stays in lockstep with the underlying state.            */
function ActiveFiltersPanel({ filters, setFilter, toggleArrayFilter, preview }) {
  const pills = useMemo(() => {
    const f = filters || {};
    const out = [];
    const pushArr = (key, label, vals) => {
      for (const v of (vals || [])) {
        out.push({ key: `${key}:${v}`, group: label, label: String(v), onRemove: () => toggleArrayFilter(key, v) });
      }
    };
    pushArr("stages", "Stage", f.stages);
    // Wave 2 / A5 — sub-status, ratings, practice type, size bands, NPPES
    // taxonomy chips for parity with the segment builder.
    pushArr("sub_statuses", "Sub-status", f.sub_statuses);
    pushArr("ratings", "Rating", f.ratings);
    pushArr("practice_types", "Practice type", f.practice_types);
    pushArr("size_bands", "Practice size", f.size_bands);
    pushArr("nppes_taxonomy", "NPPES taxonomy", f.nppes_taxonomy);
    pushArr("tiers", "Tier", f.tiers);
    pushArr("sources", "Source", f.sources);
    pushArr("product_interest", "Product", f.product_interest);
    pushArr("specialty_groups", "Specialty", f.specialty_groups);
    pushArr("states", "State", f.states);
    pushArr("ehr_vendors", "EHR", f.ehr_vendors);
    pushArr("signals", "Signal", f.signals);
    if (typeof f.score_min === "number" && f.score_min > 0)
      out.push({ key: "score_min", group: "AI score", label: `≥ ${f.score_min}`, onRemove: () => setFilter("score_min", 0) });
    if (typeof f.score_max === "number" && f.score_max < 100)
      out.push({ key: "score_max", group: "AI score", label: `≤ ${f.score_max}`, onRemove: () => setFilter("score_max", 100) });
    if (typeof f.medicare_min === "number" && f.medicare_min > 0)
      out.push({ key: "medicare_min", group: "Medicare benes", label: `≥ ${f.medicare_min}`, onRemove: () => setFilter("medicare_min", 0) });
    if (typeof f.medicare_max === "number" && f.medicare_max < 99999 && f.medicare_max > 0)
      out.push({ key: "medicare_max", group: "Medicare benes", label: `≤ ${f.medicare_max}`, onRemove: () => setFilter("medicare_max", 99999) });
    if (typeof f.group_size_min === "number" && f.group_size_min > 0)
      out.push({ key: "group_size_min", group: "Group size", label: `≥ ${f.group_size_min}`, onRemove: () => setFilter("group_size_min", 0) });
    if (typeof f.group_size_max === "number" && f.group_size_max < 9999 && f.group_size_max > 0)
      out.push({ key: "group_size_max", group: "Group size", label: `≤ ${f.group_size_max}`, onRemove: () => setFilter("group_size_max", 9999) });
    if (typeof f.last_contact_days_min === "number" && f.last_contact_days_min > 0)
      out.push({ key: "last_contact_days_min", group: "Last contact", label: `≥ ${f.last_contact_days_min}d`, onRemove: () => setFilter("last_contact_days_min", 0) });
    if (typeof f.last_contact_days_max === "number" && f.last_contact_days_max > 0 && f.last_contact_days_max < 9999)
      out.push({ key: "last_contact_days_max", group: "Last contact", label: `≤ ${f.last_contact_days_max}d`, onRemove: () => setFilter("last_contact_days_max", 9999) });
    if (typeof f.value_min === "number" && f.value_min > 0)
      out.push({ key: "value_min", group: "Deal value", label: `≥ $${Number(f.value_min).toLocaleString()}`, onRemove: () => setFilter("value_min", 0) });
    if (typeof f.value_max === "number" && f.value_max > 0 && f.value_max < 99999999)
      out.push({ key: "value_max", group: "Deal value", label: `≤ $${Number(f.value_max).toLocaleString()}`, onRemove: () => setFilter("value_max", 99999999) });
    return out;
  }, [filters, setFilter, toggleArrayFilter]);

  const clearAll = () => {
    setFilter("stages", []);
    setFilter("sub_statuses", []);
    setFilter("ratings", []);
    setFilter("practice_types", []);
    setFilter("size_bands", []);
    setFilter("nppes_taxonomy", []);
    setFilter("tiers", []);
    setFilter("sources", []);
    setFilter("product_interest", []);
    setFilter("specialty_groups", []);
    setFilter("states", []);
    setFilter("ehr_vendors", []);
    setFilter("signals", []);
    setFilter("score_min", 0);
    setFilter("score_max", 100);
    setFilter("medicare_min", 0);
    setFilter("medicare_max", 99999);
    setFilter("group_size_min", 0);
    setFilter("group_size_max", 9999);
    setFilter("last_contact_days_min", 0);
    setFilter("last_contact_days_max", 9999);
    setFilter("value_min", 0);
    setFilter("value_max", 99999999);
  };

  const previewCount = preview?.count;
  const previewLoading = preview?.loading;

  return (
    <div
      data-testid="campaign-active-filters"
      style={{
        background: "var(--surface,#fff)",
        border: "0.5px solid var(--border)",
        borderRadius: 10, padding: "10px 12px", marginBottom: 14,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: pills.length ? 8 : 0 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          Active filters
        </span>
        <span
          data-testid="campaign-active-filters-count"
          style={{
            fontSize: 10, fontWeight: 700, color: pills.length ? "#5B6CFF" : "var(--text-tertiary)",
            background: pills.length ? "#EEF2FF" : "transparent",
            padding: "1px 7px", borderRadius: 999,
          }}
        >
          {pills.length}
        </span>
        <div style={{ flex: 1 }} />
        <span
          data-testid="campaign-active-filters-leadcount"
          style={{ fontSize: 11, color: previewLoading ? "var(--text-tertiary)" : "var(--text-primary)", fontWeight: 600 }}
          title="Leads matching the current filter set"
        >
          {previewLoading
            ? "Refreshing…"
            : (typeof previewCount === "number" ? `${previewCount.toLocaleString()} lead${previewCount === 1 ? "" : "s"}` : "—")}
        </span>
        {pills.length > 0 && (
          <button
            data-testid="campaign-active-filters-clear"
            onClick={clearAll}
            style={{ background: "transparent", border: "none", color: "#E24B4A", fontSize: 10.5, fontWeight: 500, cursor: "pointer", padding: 0 }}
          >
            Clear all
          </button>
        )}
      </div>
      {pills.length === 0 ? (
        <div style={{ fontSize: 11, color: "var(--text-tertiary)", fontStyle: "italic" }}>
          No filters yet. Use the AI brief above or pick from the sections below.
        </div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
          {pills.map((p) => (
            <span
              key={p.key}
              data-testid={`campaign-active-filter-${p.key}`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5,
                padding: "3px 9px", borderRadius: 999, background: "#EEF2FF",
                border: "0.5px solid #CFD7FF", color: "#3F4FE0",
                fontSize: 10.5, fontWeight: 500,
              }}
            >
              <span style={{ opacity: 0.65, fontWeight: 700 }}>{p.group}:</span>
              <span>{p.label}</span>
              <button
                onClick={p.onRemove}
                aria-label={`Remove ${p.group} ${p.label}`}
                style={{ background: "none", border: "none", color: "#3F4FE0", cursor: "pointer", padding: 0, lineHeight: 1, opacity: 0.7 }}
              >
                <X style={{ width: 10, height: 10 }} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Tag-input chiplet ───────────────────────────────────── */
function TagInput({ values, onChange, placeholder, max = 10, testid }) {
  return (
    <>
      <input
        data-testid={`${testid}-input`}
        placeholder={placeholder}
        className="input"
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          const v = e.currentTarget.value.trim();
          if (!v) return;
          if (!values.includes(v) && values.length < max) onChange([...values, v]);
          e.currentTarget.value = "";
        }}
      />
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
        {values.map((v) => (
          <span key={v} data-testid={`${testid}-pill-${v}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, background: "var(--blue-soft)", color: "var(--blue)", fontSize: 10, fontWeight: 500 }}>
            {v}
            <button onClick={() => onChange(values.filter((x) => x !== v))} style={{ background: "none", border: "none", color: "var(--blue)", cursor: "pointer", padding: 0, lineHeight: 1 }}><X style={{ width: 9, height: 9 }} /></button>
          </span>
        ))}
      </div>
    </>
  );
}

/* ── Main component ──────────────────────────────────────── */
// ── Iter 279 (Jul 2026) — audience-preview accordion helpers ───────────
// `computeMatchedFields(lead, filters)` inspects the resolved lead doc
// against the campaign's active filters and returns a Set of field paths
// that satisfy each active filter — so we can render a small green
// "✓ matches" chip next to each contributing datapoint. This turns the
// preview into a "why did this lead match?" explainer.
function computeMatchedFields(leadDoc, filters) {
  const matched = new Set();
  if (!leadDoc || !filters) return matched;
  const stages = filters.stages || [];
  if (stages.length && stages.includes(leadDoc.stage))                 matched.add("stage");
  const sources = filters.sources || [];
  const src = (leadDoc.lead_source || leadDoc.source || "");
  if (sources.length && sources.some(s => (src || "").toLowerCase().includes(String(s).toLowerCase()))) matched.add("lead_source");
  const specs = filters.specialties || [];
  const sp = String(leadDoc.specialty || "").toLowerCase();
  if (specs.length && specs.some(x => sp.includes(String(x).toLowerCase())))  matched.add("specialty");
  const states = filters.states || [];
  if (states.length && states.includes(leadDoc.state))                 matched.add("state");
  // Medicare benes range
  const benes = Number(leadDoc?.medicare?.beneficiaries || leadDoc?.enrichment?.cms?.medicare_beneficiaries || 0);
  if (typeof filters.medicare_benes_min === "number" && filters.medicare_benes_min > 0 && benes >= filters.medicare_benes_min) matched.add("medicare.beneficiaries");
  if (typeof filters.medicare_benes_max === "number" && filters.medicare_benes_max > 0 && benes <= filters.medicare_benes_max) matched.add("medicare.beneficiaries");
  // AI score range
  const score = Number(leadDoc.ai_score || 0);
  if (typeof filters.ai_score_min === "number" && filters.ai_score_min > 0 && score >= filters.ai_score_min) matched.add("ai_score");
  // Never-contacted
  if (filters.last_contact_days_min && !leadDoc.last_contact_date) matched.add("last_contact_date");
  return matched;
}

function MatchChip() {
  return (
    <span
      data-testid="preview-lead-match-chip"
      style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 8, fontWeight: 700, letterSpacing: "0.04em", padding: "1px 6px", borderRadius: 999, background: "#ECFDF5", color: "#065F46", border: "0.5px solid #A7F3D0", marginLeft: 6, verticalAlign: "middle" }}
    >
      ✓ matches
    </span>
  );
}

function DetailRow({ label, value, matched }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "baseline", fontSize: 11, padding: "3px 0" }}>
      <div style={{ minWidth: 100, color: "#94A3B8", fontWeight: 500 }}>{label}</div>
      <div style={{ flex: 1, color: "#0F172A", fontWeight: 500, whiteSpace: "normal", overflowWrap: "anywhere" }}>
        {value}
        {matched && <MatchChip />}
      </div>
    </div>
  );
}

function AudienceLeadDetail({ leadSample, cacheEntry, filters, onExclude, canExclude, disabledExcludeReason, ownerNames }) {
  const enrichment = cacheEntry?.status === "ok" ? cacheEntry.data : null;
  const merged = enrichment || leadSample || {};
  const matched = computeMatchedFields(merged, filters);
  const ownerId = merged.owner_id || leadSample.owner_id;
  const ownerLabel = ownerId
    ? ((ownerNames && ownerNames[ownerId]) || "Assigned rep")
    : null;
  const fmtPhone = (p) => {
    const s = String(p || "").replace(/[^\d]/g, "");
    if (s.length === 10) return `(${s.slice(0, 3)}) ${s.slice(3, 6)}-${s.slice(6)}`;
    if (s.length === 11 && s.startsWith("1")) return `(${s.slice(1, 4)}) ${s.slice(4, 7)}-${s.slice(7)}`;
    return p || "";
  };
  const medicareBenes = Number(merged?.medicare?.beneficiaries || 0) || null;
  const medicareAllowed = merged?.medicare?.allowed;
  const ehrVendor = String(merged?.ehr?.vendor || "").trim();
  const ehrProduct = String(merged?.ehr?.product_name || "").trim();
  const aco = merged?.aco_participation;
  const signals = merged?.signals || {};
  const activeSignals = Object.entries(signals || {}).filter(([, v]) => v === true).map(([k]) => k);
  const location = [merged.city, merged.state, merged.county && `${merged.county} Co.`].filter(Boolean).join(", ");
  const openHref = `/lead-focus/${leadSample.id}`;
  return (
    <div
      data-testid={`preview-lead-expanded-${leadSample.id}`}
      style={{
        background: "#FFFFFF", border: "0.5px solid #CBD5E1",
        borderRadius: 8, padding: "10px 12px", marginTop: 6, marginBottom: 4,
        boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
      }}
    >
      <DetailRow label="Contact"    value={merged.contact_name} matched={false} />
      <DetailRow label="Specialty"  value={merged.specialty}    matched={matched.has("specialty")} />
      <DetailRow label="Location"   value={location} matched={matched.has("state")} />
      <DetailRow label="Phone"      value={fmtPhone(merged.phone)} />
      <DetailRow label="Email"      value={merged.email} />
      <DetailRow label="Stage"      value={(merged.stage || "").replace(/_/g, " ")} matched={matched.has("stage")} />
      <DetailRow label="Lead source" value={merged.lead_source || merged.source} matched={matched.has("lead_source")} />
      <DetailRow
        label="Owner"
        value={ownerLabel || <span style={{ color: "#94A3B8", fontStyle: "italic" }}>Unassigned</span>}
        matched={false}
      />

      <div style={{ borderTop: "0.5px solid #F1F5F9", margin: "8px 0 6px" }} />

      {cacheEntry?.status === "loading" && (
        <div style={{ fontSize: 11, color: "#94A3B8", padding: "4px 0", display: "flex", alignItems: "center", gap: 6 }}>
          <Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} />
          Loading enrichment…
        </div>
      )}
      {cacheEntry?.status === "error" && (
        <div style={{ fontSize: 11, color: "#B91C1C", padding: "4px 0" }}>
          Couldn&apos;t load enrichment — {cacheEntry.error || "unknown error"}.
        </div>
      )}
      {cacheEntry?.status === "ok" && (
        <>
          <DetailRow label="Medicare benes"   value={medicareBenes !== null ? medicareBenes.toLocaleString() : null} matched={matched.has("medicare.beneficiaries")} />
          <DetailRow label="Medicare allowed" value={medicareAllowed != null ? `$${Number(medicareAllowed).toLocaleString(undefined, { maximumFractionDigits: 0 })}` : null} />
          <DetailRow label="EHR vendor"       value={ehrVendor || ehrProduct || null} matched={matched.has("ehr.vendor")} />
          <DetailRow label="ACO participation" value={aco === true ? "Yes" : aco === false ? "No" : null} />
          <DetailRow label="Signals"          value={activeSignals.length ? activeSignals.join(", ").replace(/_/g, " ") : null} />
          <DetailRow label="AI score"         value={typeof merged.ai_score === "number" ? merged.ai_score : null} matched={matched.has("ai_score")} />
          <DetailRow label="Last contacted"   value={merged.last_contact_date ? new Date(merged.last_contact_date).toLocaleDateString() : "Never"} matched={matched.has("last_contact_date")} />
        </>
      )}

      <div style={{ display: "flex", gap: 8, marginTop: 10, paddingTop: 8, borderTop: "0.5px solid #F1F5F9" }}>
        <a
          href={openHref}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`preview-lead-open-full-${leadSample.id}`}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600,
            padding: "5px 10px", borderRadius: 6, border: "0.5px solid #2563EB",
            color: "#2563EB", textDecoration: "none", background: "#fff",
          }}
        >
          <ExternalLink style={{ width: 11, height: 11 }} /> Open full profile
        </a>
        <button
          onClick={() => onExclude(leadSample.id)}
          disabled={!canExclude}
          data-testid={`preview-lead-exclude-${leadSample.id}`}
          title={canExclude ? "Remove this lead from the audience" : (disabledExcludeReason || "Exclude available on AI-pinned audiences. Refine filters to narrow the pool.")}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600,
            padding: "5px 10px", borderRadius: 6,
            border: `0.5px solid ${canExclude ? "#B45309" : "#E2E8F0"}`,
            color: canExclude ? "#B45309" : "#94A3B8",
            background: "#fff", cursor: canExclude ? "pointer" : "not-allowed",
            fontFamily: "inherit",
          }}
        >
          <MinusCircle style={{ width: 11, height: 11 }} /> Exclude from list
        </button>
      </div>
    </div>
  );
}

export default function CampaignAudienceBuilder({
  campaignFilters, setFilter, toggleArrayFilter,
  campaignPresets, applyPreset, savePresetLocally, activePresetId, presetDefaults,
  campaignPreview, onLuminaPin, onExcludeLead, onLoadMoreAudience,
}) {
  const f = campaignFilters;

  const onAIApply = (next) => {
    Object.entries(next).forEach(([k, v]) => setFilter(k, v));
  };

  // ── Iter 279 (Jul 2026) — audience preview accordion ─────────────
  // Single-open expansion: click any sample row to see full lead
  // context (specialty, city/state, phone, email, Medicare benes,
  // EHR vendor, ACO participation, signals). Detail is fetched on
  // demand from GET /api/leads/{id}?include=vendor (already-existing
  // endpoint that merges the sibling `leads_ampliz` payload). Cache
  // is keyed by lead id and cleared whenever the preview sample
  // identity changes (i.e. after a fresh AI parse / filter change).
  const [expandedLeadId, setExpandedLeadId] = useState(null);
  const [detailCache, setDetailCache] = useState({});   // { leadId: {status, data, error} }
  // Owner directory (Jun 2026) — resolve owner_id → full name for the
  // preview badge + expanded "Owner" line. `/users/directory` is
  // available to any authenticated user (safe identity fields only).
  const [ownerNames, setOwnerNames] = useState({});     // { userId: full_name }
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
        const tk = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
        const res = await axios.get(`${API}/users/directory?include_inactive=true`, { headers: tk ? { Authorization: `Bearer ${tk}` } : {} });
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : (res.data?.users || []);
        const map = {};
        for (const u of list) { if (u?.id) map[u.id] = u.full_name || u.email || "Assigned rep"; }
        setOwnerNames(map);
      } catch { /* best-effort — badge falls back to a neutral label */ }
    })();
    return () => { cancelled = true; };
  }, []);
  // Iter 281 (Jul 2026) — preview browse controls: reveal 20 more
  // rows per click, plus a local search that filters the revealed
  // rows client-side. `visibleCount` starts at 5 (same as before);
  // `searchQ` is debounced by 150ms via `searchQDebounced` so a fast
  // typer doesn't re-render on every keystroke.
  const [visibleCount, setVisibleCount] = useState(5);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchQDebounced, setSearchQDebounced] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearchQDebounced(searchQ.trim().toLowerCase()), 150);
    return () => clearTimeout(t);
  }, [searchQ]);
  // Clear cache when a fresh audience is loaded so a re-parse doesn't
  // display stale enrichment against a new sample set. Same trigger
  // resets browse state so the rep starts fresh.
  const sampleFingerprint = (campaignPreview?.sample || []).map(l => l.id).join("|");
  useEffect(() => {
    setExpandedLeadId(null);
    setDetailCache({});
    setVisibleCount(5);
    setSearchQ("");
    setSearchQDebounced("");
  }, [sampleFingerprint]);

  const toggleExpanded = async (leadId) => {
    if (expandedLeadId === leadId) {
      setExpandedLeadId(null);
      return;
    }
    setExpandedLeadId(leadId);
    if (detailCache[leadId]?.status === "ok") return;   // already cached
    setDetailCache(prev => ({ ...prev, [leadId]: { status: "loading" } }));
    try {
      const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      const tk = (typeof localStorage !== "undefined" && localStorage.getItem("token")) || "";
      const res = await axios.get(`${API}/leads/${leadId}?include=vendor`, {
        headers: tk ? { Authorization: `Bearer ${tk}` } : {},
        timeout: 12000,
      });
      setDetailCache(prev => ({ ...prev, [leadId]: { status: "ok", data: res.data } }));
    } catch (e) {
      setDetailCache(prev => ({ ...prev, [leadId]: { status: "error", error: e?.message || "Fetch failed" } }));
    }
  };

  const handleExclude = (leadId) => {
    if (!onExcludeLead) {
      toast.info("Exclude isn't available in this campaign mode yet.");
      return;
    }
    // Confirm gently to avoid mis-clicks (there's no undo in v1).
    onExcludeLead(leadId);
    setExpandedLeadId(null);
    setDetailCache(prev => {
      const n = { ...prev };
      delete n[leadId];
      return n;
    });
  };

  return (
    <div data-testid="campaign-step2" style={{ display: "grid", gridTemplateColumns: "1.3fr 0.85fr", gap: 18 }}>
      {/* ── Left: AI panel + filter groups (modal body owns the scroll) ── */}
      <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

        <AIBriefPanel filters={f} onApply={onAIApply} onLuminaPin={onLuminaPin} />

        <ActiveFiltersPanel
          filters={f}
          setFilter={setFilter}
          toggleArrayFilter={toggleArrayFilter}
          preview={campaignPreview}
        />

        <FieldGroup eyebrow="Presets" hint={`${(presetDefaults || []).length + (campaignPresets || []).length} saved`}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="campaign-presets">
            {[...(presetDefaults || []), ...(campaignPresets || [])].map((p) => (
              <ChipBtn key={p.id} testid={`campaign-preset-${p.id}`} active={activePresetId === p.id} onClick={() => applyPreset(p)} tone="blue">
                {p.label}
              </ChipBtn>
            ))}
            <button data-testid="campaign-save-preset" onClick={savePresetLocally} style={{ padding: "4px 11px", borderRadius: 999, fontSize: 11, fontWeight: 500, border: "0.5px dashed var(--border)", background: "transparent", color: "var(--text-3)", cursor: "pointer", fontFamily: "inherit" }}>
              + Save current
            </button>
          </div>
        </FieldGroup>

        {/* Lead taxonomy */}
        <div className="card" style={{ padding: 14, border: "0.5px solid var(--border)" }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 12, fontWeight: 600, color: "var(--navy)", marginBottom: 10, letterSpacing: "-0.005em" }}>Lead status</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <FieldGroup eyebrow="Stage">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="filter-stages">
                {STAGES.map((s) => <ChipBtn key={s} testid={`filter-stage-${s}`} active={(f.stages || []).includes(s)} onClick={() => toggleArrayFilter("stages", s)} tone="success">{s.replace(/_/g, " ")}</ChipBtn>)}
              </div>
            </FieldGroup>

            {/* Wave 2 / A5 — Sub-status parity with segment builder.
                Options derive from `pipelineStages.SUB_STATUSES` for the
                stages the user has chip-selected (or all stages if none). */}
            {(() => {
              const stages = (f.stages && f.stages.length) ? f.stages : Object.keys(SUB_STATUSES || {});
              const subs = Array.from(new Set(
                stages.flatMap((s) => (SUB_STATUSES?.[s] || []).map((o) => o.value))
              ));
              if (subs.length === 0) return null;
              return (
                <FieldGroup eyebrow={`Sub-status${f.stages?.length ? ` (filtered by selected stage${f.stages.length === 1 ? "" : "s"})` : ""}`}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 180, overflowY: "auto" }} data-testid="filter-sub-statuses">
                    {subs.map((s) => (
                      <ChipBtn
                        key={s}
                        testid={`filter-sub-status-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        active={(f.sub_statuses || []).includes(s)}
                        onClick={() => toggleArrayFilter("sub_statuses", s)}
                        tone="blue"
                      >{s}</ChipBtn>
                    ))}
                  </div>
                </FieldGroup>
              );
            })()}

            <FieldGroup eyebrow="Lead rating">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="filter-ratings">
                {RATINGS.map((r) => (
                  <ChipBtn
                    key={r}
                    testid={`filter-rating-${r.toLowerCase()}`}
                    active={(f.ratings || []).includes(r)}
                    onClick={() => toggleArrayFilter("ratings", r)}
                    tone={r === "Hot" ? "danger" : r === "Warm" ? "accent" : "blue"}
                  >{r}</ChipBtn>
                ))}
              </div>
            </FieldGroup>

            <FieldGroup eyebrow="Tier">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="filter-tiers">
                {TIERS.map((t) => <ChipBtn key={t} testid={`filter-tier-${t}`} active={(f.tiers || []).includes(t)} onClick={() => toggleArrayFilter("tiers", t)} tone="accent">Tier {t}</ChipBtn>)}
              </div>
            </FieldGroup>

            <FieldGroup eyebrow="Lead source">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="filter-sources">
                {SOURCES.map((s) => <ChipBtn key={s} testid={`filter-source-${s}`} active={(f.sources || []).includes(s)} onClick={() => toggleArrayFilter("sources", s)} tone="blue">{s}</ChipBtn>)}
              </div>
            </FieldGroup>

            <FieldGroup eyebrow="Product interest">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {PRODUCTS.map((p) => <ChipBtn key={p} testid={`filter-product-${p}`} active={(f.product_interest || []).includes(p)} onClick={() => toggleArrayFilter("product_interest", p)} tone="accent">{p.toUpperCase()}</ChipBtn>)}
              </div>
            </FieldGroup>
          </div>
        </div>

        {/* NPPES + practice */}
        <div className="card" style={{ padding: 14, border: "0.5px solid var(--border)" }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 12, fontWeight: 600, color: "var(--navy)", marginBottom: 10 }}>NPPES & practice profile</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <FieldGroup eyebrow="Specialty / specialty group">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="filter-specialties">
                {SPECIALTIES.map((s) => <ChipBtn key={s} testid={`filter-specialty-${s.toLowerCase().replace(/\s+/g, "-")}`} active={(f.specialty_groups || []).includes(s)} onClick={() => toggleArrayFilter("specialty_groups", s)} tone="blue">{s}</ChipBtn>)}
              </div>
            </FieldGroup>

            {/* Wave 2 / A5 — Practice type parity with segment builder. */}
            <FieldGroup eyebrow="Practice type">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="filter-practice-types">
                {PRACTICE_TYPES.map((p) => (
                  <ChipBtn
                    key={p}
                    testid={`filter-practice-type-${p.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    active={(f.practice_types || []).includes(p)}
                    onClick={() => toggleArrayFilter("practice_types", p)}
                    tone="teal"
                  >{p}</ChipBtn>
                ))}
              </div>
            </FieldGroup>

            {/* Wave 2 / A5 — Practice size band parity. */}
            <FieldGroup eyebrow="Practice size band">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="filter-size-bands">
                {SIZE_BANDS.map((sz) => (
                  <ChipBtn
                    key={sz}
                    testid={`filter-size-band-${sz.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    active={(f.size_bands || []).includes(sz)}
                    onClick={() => toggleArrayFilter("size_bands", sz)}
                    tone="accent"
                  >{sz}</ChipBtn>
                ))}
              </div>
            </FieldGroup>

            {/* Wave 2 / A5 — NPPES taxonomy chip input parity. */}
            <FieldGroup eyebrow="NPPES taxonomy (Enter to add)">
              <TagInput
                testid="campaign-filter-nppes-taxonomy"
                values={f.nppes_taxonomy || []}
                onChange={(v) => setFilter("nppes_taxonomy", v)}
                placeholder="e.g. 207RC0000X · type or paste codes"
                max={20}
              />
            </FieldGroup>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <FieldGroup eyebrow="Group size (FTE) ≥">
                <input data-testid="filter-group-size-min" type="number" className="input num" placeholder="e.g. 5" value={f.group_size_min ?? ""} onChange={(e) => setFilter("group_size_min", e.target.value ? Number(e.target.value) : null)} />
              </FieldGroup>
              <FieldGroup eyebrow="Years in practice ≥">
                <input data-testid="filter-years-min" type="number" className="input num" placeholder="e.g. 10" value={f.years_min ?? ""} onChange={(e) => setFilter("years_min", e.target.value ? Number(e.target.value) : null)} />
              </FieldGroup>
            </div>
          </div>
        </div>

        {/* Medicare + EHR */}
        <div className="card" style={{ padding: 14, border: "0.5px solid var(--border)" }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 12, fontWeight: 600, color: "var(--navy)", marginBottom: 10 }}>Medicare & EHR signals</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <FieldGroup eyebrow="Medicare benes ≥">
                <input data-testid="filter-medicare-min" type="number" className="input num" placeholder="e.g. 500" value={f.medicare_min ?? ""} onChange={(e) => setFilter("medicare_min", e.target.value ? Number(e.target.value) : null)} />
              </FieldGroup>
              <FieldGroup eyebrow="Medicare benes ≤">
                {/* Wave 2 / A5 — explicit max input (was state-only, no UI). */}
                <input
                  data-testid="filter-medicare-max"
                  type="number"
                  className="input num"
                  placeholder="e.g. 200"
                  value={(f.medicare_max != null && f.medicare_max < 99999) ? f.medicare_max : ""}
                  onChange={(e) => setFilter("medicare_max", e.target.value ? Number(e.target.value) : 99999)}
                />
              </FieldGroup>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <FieldGroup eyebrow="Medicare allowed ≥ ($)">
                <input data-testid="filter-medicare-allowed-min" type="number" className="input num" placeholder="e.g. 100000" value={f.medicare_allowed_min ?? ""} onChange={(e) => setFilter("medicare_allowed_min", e.target.value ? Number(e.target.value) : null)} />
              </FieldGroup>
              <FieldGroup eyebrow="Medicare allowed ≤ ($)">
                <input
                  data-testid="filter-medicare-allowed-max"
                  type="number"
                  className="input num"
                  placeholder="e.g. 500000"
                  value={f.medicare_allowed_max || ""}
                  onChange={(e) => setFilter("medicare_allowed_max", e.target.value ? Number(e.target.value) : 0)}
                />
              </FieldGroup>
            </div>

            <FieldGroup eyebrow="EHR vendor">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="filter-ehr-vendors">
                {EHR_VENDORS.map((v) => <ChipBtn key={v} testid={`filter-ehr-${v.toLowerCase().replace(/\s+/g, "-")}`} active={(f.ehr_vendors || []).includes(v)} onClick={() => toggleArrayFilter("ehr_vendors", v)} tone="neutral">{v}</ChipBtn>)}
              </div>
            </FieldGroup>

            <FieldGroup eyebrow="Participation signals">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }} data-testid="filter-signals">
                {SIGNAL_FIELDS.map((s) => <ChipBtn key={s.id} testid={`filter-signal-${s.id}`} active={(f.signals || []).includes(s.id)} onClick={() => toggleArrayFilter("signals", s.id)} tone="success">{s.label}</ChipBtn>)}
              </div>
            </FieldGroup>
          </div>
        </div>

        {/* Score + value + activity + owner */}
        <div className="card" style={{ padding: 14, border: "0.5px solid var(--border)" }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 12, fontWeight: 600, color: "var(--navy)", marginBottom: 10 }}>Pipeline scoring</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <FieldGroup eyebrow={`AI score range: ${f.score_min ?? 0} – ${f.score_max ?? 100}`}>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <input data-testid="filter-score-min" type="range" min="0" max="100" value={f.score_min ?? 0} onChange={(e) => setFilter("score_min", Number(e.target.value))} style={{ flex: 1, accentColor: "var(--blue)" }} />
                <input data-testid="filter-score-max" type="range" min="0" max="100" value={f.score_max ?? 100} onChange={(e) => setFilter("score_max", Number(e.target.value))} style={{ flex: 1, accentColor: "var(--blue)" }} />
              </div>
            </FieldGroup>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <FieldGroup eyebrow="Min pipeline value ($)">
                <input data-testid="filter-value-min" type="number" className="input num" placeholder="e.g. 50000" value={f.value_min ?? ""} onChange={(e) => setFilter("value_min", e.target.value ? Number(e.target.value) : null)} />
              </FieldGroup>
              <FieldGroup eyebrow="Max pipeline value ($)">
                {/* Wave 2 / A5 — explicit max parity with segment builder range. */}
                <input
                  data-testid="filter-value-max"
                  type="number"
                  className="input num"
                  placeholder="e.g. 500000"
                  value={(f.value_max != null && f.value_max < 99999999) ? f.value_max : ""}
                  onChange={(e) => setFilter("value_max", e.target.value ? Number(e.target.value) : 99999999)}
                />
              </FieldGroup>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <FieldGroup eyebrow="Owner">
                <select data-testid="filter-owner" className="select" value={f.owner || "any"} onChange={(e) => setFilter("owner", e.target.value)}>
                  <option value="any">Any owner</option>
                  <option value="me">Only mine</option>
                </select>
              </FieldGroup>
              <FieldGroup eyebrow="Last contact (days ago) ≥">
                {/* Wave 2 / A5 — pair with the existing min for a true range. */}
                <input
                  data-testid="filter-last-contact-days-min"
                  type="number"
                  className="input num"
                  placeholder="e.g. 30"
                  value={f.last_contact_days_min || ""}
                  onChange={(e) => setFilter("last_contact_days_min", e.target.value ? Number(e.target.value) : 0)}
                />
              </FieldGroup>
            </div>

            <FieldGroup eyebrow="Activity">
              <select data-testid="filter-activity" className="select" value={f.activity || "any"} onChange={(e) => setFilter("activity", e.target.value)}>
                <option value="any">Any</option>
                <option value="active_30d">Active in last 30d</option>
                <option value="no_activity_30d">No activity in 30d</option>
                <option value="no_activity_60d">Lapsed (60d+)</option>
              </select>
            </FieldGroup>
          </div>
        </div>

        {/* Geography */}
        <div className="card" style={{ padding: 14, border: "0.5px solid var(--border)" }}>
          <div style={{ fontFamily: "var(--font-head)", fontSize: 12, fontWeight: 600, color: "var(--navy)", marginBottom: 10 }}>Address / location <span style={{ fontSize: 10, fontWeight: 400, color: "var(--text-4)", marginLeft: 6 }}>(mirrors Lead Focus)</span></div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }} data-testid="campaign-location-filters">
            <FieldGroup eyebrow="States">
              <select
                data-testid="campaign-filter-state"
                onChange={(e) => {
                  const v = e.target.value;
                  if (!v) return;
                  const list = f.states || [];
                  if (!list.includes(v)) setFilter("states", [...list, v]);
                  e.target.value = "";
                }}
                className="select"
              >
                <option value="">+ Add state…</option>
                {STATES_LIST.filter((s) => !(f.states || []).includes(s)).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                {(f.states || []).map((s) => (
                  <span key={s} data-testid={`campaign-filter-state-pill-${s}`} style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 999, background: "var(--blue-soft)", color: "var(--blue)", fontSize: 10, fontWeight: 500 }}>
                    {s}
                    <button onClick={() => setFilter("states", (f.states || []).filter((x) => x !== s))} style={{ background: "none", border: "none", color: "var(--blue)", cursor: "pointer", padding: 0, lineHeight: 1 }}><X style={{ width: 9, height: 9 }} /></button>
                  </span>
                ))}
              </div>
            </FieldGroup>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <FieldGroup eyebrow="City (Enter to add, max 10)">
                <TagInput testid="campaign-filter-cities" values={f.cities || []} onChange={(v) => setFilter("cities", v)} placeholder="Chicago" />
              </FieldGroup>
              <FieldGroup eyebrow="ZIP (Enter to add, max 10)">
                <TagInput testid="campaign-filter-zips" values={f.zips || []} onChange={(v) => setFilter("zips", v)} placeholder="60601" />
              </FieldGroup>
            </div>
          </div>
        </div>
      </div>

      {/* ── Right: live audience preview ── */}
      <div style={{ position: "sticky", top: 0, alignSelf: "flex-start" }}>
        <div data-testid="campaign-preview" className="card" style={{ padding: 18, border: "0.5px solid var(--border)", minHeight: 320, display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <Target style={{ width: 14, height: 14, color: "var(--blue)" }} />
            <span style={{ fontFamily: "var(--font-head)", fontSize: 13, fontWeight: 600, color: "var(--navy)" }}>Audience preview</span>
            {campaignPreview?.luminaPinned && !campaignPreview?.empty && (
              <span data-testid="campaign-preview-lumina-pin" title="Pinned to Lumina audience — change any filter to release" style={{ marginLeft: 6, fontSize: 9, padding: "2px 7px", borderRadius: 999, background: "var(--blue-soft, #E6F1FB)", color: "var(--blue, #185FA5)", fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
                ✦ Lumina pinned
              </span>
            )}
            {campaignPreview?.loading && <Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite", color: "var(--text-3)", marginLeft: "auto" }} />}
          </div>

          {/* Iter 280 (Jul 2026) — empty-intent placeholder. Rendered
              instead of the count/sample block when the rep hasn't
              expressed audience intent yet (no AI parse, no preset,
              no filter toggled). Prevents the "4,977 leads match" with
              random-looking sample rows on Step 2 open. */}
          {campaignPreview?.empty ? (
            <div
              data-testid="preview-empty-state"
              style={{
                flex: 1, display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "center",
                padding: "20px 12px", textAlign: "center", gap: 14,
              }}
            >
              <div
                style={{
                  width: 44, height: 44, borderRadius: "50%",
                  background: "var(--blue-soft, #E6F1FB)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <Target style={{ width: 20, height: 20, color: "var(--blue, #185FA5)", opacity: 0.7 }} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--navy)", letterSpacing: "-0.01em" }}>
                Define your audience to preview leads
              </div>
              <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 8, fontSize: 11, color: "var(--text-3)", maxWidth: 260 }}>
                <li style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span aria-hidden style={{ color: "var(--blue, #185FA5)", fontWeight: 700, minWidth: 12 }}>✦</span>
                  <span>Describe your audience in plain English</span>
                </li>
                <li style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span aria-hidden style={{ color: "var(--text-4)", fontWeight: 700, minWidth: 12 }}>—&nbsp;or&nbsp;—</span>
                </li>
                <li style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span aria-hidden style={{ color: "var(--blue, #185FA5)", fontWeight: 700, minWidth: 12 }}>●</span>
                  <span>Pick a preset chip</span>
                </li>
                <li style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span aria-hidden style={{ color: "var(--text-4)", fontWeight: 700, minWidth: 12 }}>—&nbsp;or&nbsp;—</span>
                </li>
                <li style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span aria-hidden style={{ color: "var(--blue, #185FA5)", fontWeight: 700, minWidth: 12 }}>▷</span>
                  <span>Toggle any filter on the left</span>
                </li>
              </ul>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 14 }}>
                <span data-testid="campaign-preview-count" className="num" style={{ fontSize: 36, fontWeight: 700, color: "var(--navy)", letterSpacing: "-0.02em", lineHeight: 1 }}>
                  {campaignPreview?.count ?? 0}{campaignPreview?.capped ? "+" : ""}
                </span>
                <span style={{ fontSize: 11, color: "var(--text-3)" }}>leads match{campaignPreview?.capped ? " · audience is larger — adjust filters to narrow" : ""}</span>
              </div>
              <div style={{ borderTop: "0.5px solid var(--border-soft)", paddingTop: 14, display: "flex", flexDirection: "column", gap: 6, flex: 1, overflowY: "auto", maxHeight: expandedLeadId ? 560 : 360, transition: "max-height 160ms ease" }}>
                {/* Iter 281 (Jul 2026) — local search over the client-
                    cached sample. Doesn't hit the API; filters the
                    revealed rows on title / contact_name / city /
                    state / specialty (case-insensitive substring). */}
                {(campaignPreview?.sample?.length || 0) > 5 && (
                  <input
                    type="text"
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    data-testid="preview-search-input"
                    placeholder="Search this preview…"
                    style={{
                      fontSize: 11, fontFamily: "inherit",
                      padding: "6px 8px", borderRadius: 6,
                      border: "0.5px solid var(--border)",
                      background: "var(--paper)", color: "var(--text-1)",
                      marginBottom: 4, outline: "none",
                    }}
                  />
                )}
                {(() => {
                  const fullSample = campaignPreview?.sample || [];
                  const revealed   = fullSample.slice(0, visibleCount);
                  const q          = searchQDebounced;
                  const filtered   = q
                    ? revealed.filter((l) => {
                        const hay = [l.title, l.contact_name, l.city, l.state, l.specialty]
                          .filter(Boolean).join(" ").toLowerCase();
                        return hay.includes(q);
                      })
                    : revealed;
                  const totalMatched      = Number(campaignPreview?.count || 0);
                  const canRevealFromCache = revealed.length < fullSample.length;
                  const cacheExhausted    = !canRevealFromCache;
                  const hasMoreServer     = fullSample.length < totalMatched;
                  const canFetchMore      = cacheExhausted && hasMoreServer;
                  const showLoadMore      = canRevealFromCache || canFetchMore;
                  return (
                    <>
                      {(!fullSample.length && !campaignPreview?.loading) && (
                        <div style={{ fontSize: 11, color: "var(--text-4)", textAlign: "center", padding: "20px 0", fontStyle: "italic" }}>No leads match the current filters yet.</div>
                      )}
                      {fullSample.length > 0 && filtered.length === 0 && q && (
                        <div style={{ fontSize: 11, color: "var(--text-4)", textAlign: "center", padding: "12px 0", fontStyle: "italic" }}>
                          No matches in the {revealed.length.toLocaleString()} loaded lead{revealed.length === 1 ? "" : "s"}. Load more to search further.
                        </div>
                      )}
                      {filtered.map((l) => {
                        const isOpen = expandedLeadId === l.id;
                        const canExclude = !!campaignPreview?.luminaPinned;
                        return (
                          <div key={l.id} data-testid={`preview-lead-row-${l.id}`}>
                            <button
                              type="button"
                              onClick={() => toggleExpanded(l.id)}
                              data-testid={`preview-lead-toggle-${l.id}`}
                              aria-expanded={isOpen}
                              aria-controls={`preview-lead-expanded-${l.id}`}
                              style={{
                                width: "100%", textAlign: "left", background: isOpen ? "#F0F9FF" : "var(--subtle)",
                                border: `0.5px solid ${isOpen ? "#93C5FD" : "var(--border-soft)"}`,
                                borderRadius: 8, padding: "8px 10px",
                                display: "flex", alignItems: "center", gap: 8,
                                cursor: "pointer", fontFamily: "inherit",
                                transition: "background-color 120ms ease, border-color 120ms ease",
                              }}
                            >
                              {isOpen
                                ? <ChevronDown  style={{ width: 12, height: 12, color: "#2563EB", flexShrink: 0 }} />
                                : <ChevronRight style={{ width: 12, height: 12, color: "var(--text-3)", flexShrink: 0 }} />}
                              <div style={{ flex: 1, fontSize: 11, fontWeight: 600, color: "var(--text-2)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                {l.title || l.contact_name || "—"}
                              </div>
                              <span style={{ fontSize: 9, padding: "1px 7px", background: "var(--paper)", color: "var(--text-3)", borderRadius: 999, textTransform: "capitalize", border: "0.5px solid var(--border)" }}>
                                {(l.stage || "—").replace(/_/g, " ")}
                              </span>
                              {(() => {
                                const on = l.owner_id ? (ownerNames[l.owner_id] || "Assigned rep") : null;
                                return (
                                  <span
                                    data-testid={`preview-lead-owner-${l.id}`}
                                    title={on ? `Owner: ${on}` : "Unassigned"}
                                    style={{
                                      fontSize: 9, padding: "1px 7px", borderRadius: 999, border: "0.5px solid",
                                      maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0,
                                      ...(on
                                        ? { background: "#EEF2FF", color: "#4338CA", borderColor: "#C7D2FE" }
                                        : { background: "#F1F5F9", color: "#94A3B8", borderColor: "#E2E8F0", fontStyle: "italic" }),
                                    }}
                                  >
                                    {on || "Unassigned"}
                                  </span>
                                );
                              })()}
                              <span className="num" style={{ fontSize: 10, color: "var(--text-3)", fontWeight: 600, minWidth: 22, textAlign: "right" }}>
                                {getLeadScore(l) || ""}
                              </span>
                            </button>
                            {isOpen && (
                              <AudienceLeadDetail
                                leadSample={l}
                                cacheEntry={detailCache[l.id]}
                                filters={f}
                                ownerNames={ownerNames}
                                onExclude={handleExclude}
                                canExclude={canExclude}
                                disabledExcludeReason={
                                  campaignPreview?.luminaPinned
                                    ? null
                                    : "Exclude is available on AI-pinned audiences. Refine filters to narrow the pool instead."
                                }
                              />
                            )}
                          </div>
                        );
                      })}
                      {fullSample.length > 0 && (
                        <div
                          data-testid="preview-showing-count"
                          style={{ fontSize: 10, color: "var(--text-4)", textAlign: "center", marginTop: 6, paddingBottom: 2 }}
                        >
                          {q
                            ? `${filtered.length.toLocaleString()} of ${revealed.length.toLocaleString()} loaded match "${q}"`
                            : `Showing ${revealed.length.toLocaleString()} of ${totalMatched.toLocaleString()}`}
                        </div>
                      )}
                      {showLoadMore && (
                        <button
                          type="button"
                          onClick={async () => {
                            if (canRevealFromCache) {
                              setVisibleCount((v) => Math.min(v + 20, fullSample.length));
                              return;
                            }
                            if (canFetchMore && !loadingMore) {
                              setLoadingMore(true);
                              try {
                                await onLoadMoreAudience?.();
                                setVisibleCount((v) => v + 20);
                              } finally {
                                setLoadingMore(false);
                              }
                            }
                          }}
                          disabled={loadingMore}
                          data-testid="preview-load-more-btn"
                          style={{
                            marginTop: 6, alignSelf: "center",
                            background: "var(--paper)", color: "var(--blue, #185FA5)",
                            border: "0.5px solid var(--blue, #185FA5)",
                            borderRadius: 999, padding: "5px 14px",
                            fontSize: 11, fontWeight: 600, cursor: loadingMore ? "wait" : "pointer",
                            fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 6,
                          }}
                        >
                          {loadingMore
                            ? <><Loader2 style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} /> Loading more…</>
                            : `Load 20 more`}
                        </button>
                      )}
                    </>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
